import React from 'react';

import {Input, Label, Menu, Text} from '@albert-io/atomic';
import {FilterSection, FiltersMenuShell} from 'client/TeacherDashboard/components/FiltersMenu';
import {
  ASSIGNMENT_TYPE_FILTER_OPTIONS,
  CREATED_BY_FILTER_OPTIONS
} from 'client/TeacherDashboard/config/filterOptions';
import type {AssignmentTypeFilter, ClassworkCreatedByFilter} from 'client/TeacherDashboard/types';

interface Props {
  createdBy: ClassworkCreatedByFilter;
  assignmentType: AssignmentTypeFilter;
  /** Due-date range bounds as YYYY-MM-DD (native date-input format); '' = unset. */
  dueDateStart: string;
  dueDateEnd: string;
  disabled?: boolean;
  onChange: (value: ClassworkCreatedByFilter) => void;
  onAssignmentTypeChange: (value: AssignmentTypeFilter) => void;
  onDueDateChange: (bound: 'start' | 'end', value: string) => void;
}

export const FiltersMenu = ({
  createdBy,
  assignmentType,
  dueDateStart,
  dueDateEnd,
  disabled,
  onChange,
  onAssignmentTypeChange,
  onDueDateChange
}: Props) => {
  const isActive =
    createdBy !== 'all' || assignmentType !== 'all' || !!dueDateStart || !!dueDateEnd;
  return (
    <FiltersMenuShell
      closeOnItemClick
      data-testid='filters-trigger'
      disabled={disabled}
      isActive={isActive}
    >
      <FilterSection
        label='Created By'
        options={CREATED_BY_FILTER_OPTIONS}
        selected={createdBy}
        onSelect={onChange}
      />
      <Menu.Divider />
      <FilterSection
        label='Type'
        options={ASSIGNMENT_TYPE_FILTER_OPTIONS}
        selected={assignmentType}
        onSelect={onAssignmentTypeChange}
      />
      <Menu.Divider />
      {/* Raw date inputs (not Menu.Items) so interacting with them doesn't trip closeOnItemClick.
          Menu.Content's keydown handler hijacks arrows (segment stepping) and Tab (closes the
          menu) — keep those native inside this section; Escape still bubbles up to close. */}
      <div
        className='gap-x1 px-x2 py-x1 flex flex-col'
        onKeyDown={(event) => {
          if (event.key !== 'Escape') {
            event.stopPropagation();
          }
        }}
      >
        <Text color='secondary' size='s'>
          Due Date
        </Text>
        <Label className='gap-x0-half text-s flex flex-col'>
          Start
          <Input
            type='date'
            value={dueDateStart}
            max={dueDateEnd || undefined}
            onChange={(e) => onDueDateChange('start', e.target.value)}
          />
        </Label>
        <Label className='gap-x0-half text-s flex flex-col'>
          End
          <Input
            type='date'
            value={dueDateEnd}
            min={dueDateStart || undefined}
            onChange={(e) => onDueDateChange('end', e.target.value)}
          />
        </Label>
      </div>
    </FiltersMenuShell>
  );
};
